import { useEffect, useRef } from 'react';
import { applyZoom, loadZoom, saveZoom, stepFrom, DEFAULT_ZOOM } from '../utils/appZoom';

/**
 * Ctrl/Cmd + wheel and Ctrl/Cmd + `+` / `-` / `0` zoom the content, the way
 * every browser does — remembered per rail page (`page` = App's activeTab, like
 * a browser remembers zoom per site). Mounted once from `App`.
 *
 * **No `isEditableTarget` guard, deliberately.** Every other global shortcut in
 * this app bails inside a text field so typing is never stolen; zoom is the
 * opposite case — a user squinting at a request body wants the zoom while their
 * cursor is in it, and no editor here binds these chords for anything else.
 *
 * **`passive: false` on the wheel listener is load-bearing.** Without it the
 * `preventDefault` is ignored and the webview runs its own pinch-zoom on top of
 * ours, zooming twice per gesture.
 */
export function useAppZoom(page) {
  // Each rail page keeps its own zoom (owner, 13.10.2026): the listeners read
  // the page through a ref so they are bound once.
  const pageRef = useRef(page);
  pageRef.current = page;

  useEffect(() => {
    applyZoom(loadZoom(page));
    try {
      window.dispatchEvent(new Event('resize'));
    } catch {
      /* jsdom or a torn-down window */
    }
  }, [page]);

  useEffect(() => {
    // Restore before the first paint the user can notice.
    applyZoom(loadZoom(pageRef.current));

    // Anything that sizes itself by MEASURING its container has to be told.
    // xterm's fit addon is the one that shows: the terminal keeps its old
    // cols/rows and the text runs under the edge of a zoomed panel until
    // something makes it re-measure. A resize event is what those listeners
    // already wait for, so this needs no new wiring per panel.
    const settle = () => {
      try {
        window.dispatchEvent(new Event('resize'));
      } catch {
        /* jsdom or a torn-down window: the zoom itself has already applied */
      }
    };

    const bump = (direction) => {
      const next = stepFrom(loadZoom(pageRef.current), direction);
      saveZoom(next, pageRef.current);
      applyZoom(next);
      settle();
    };

    const onWheel = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      if (!event.deltaY) return;
      event.preventDefault();
      // deltaY is NEGATIVE scrolling up/away, which is zoom IN.
      bump(event.deltaY < 0 ? 1 : -1);
    };

    const onKey = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      if (event.altKey) return;
      // `key` is what the layout produces ('+' needs Shift on many, '=' on
      // US); `code` catches the numpad, where `key` is the same character but
      // the chord is the one users actually press on a full keyboard.
      const { key, code } = event;
      if (key === '+' || key === '=' || code === 'NumpadAdd') {
        event.preventDefault();
        bump(1);
      } else if (key === '-' || key === '_' || code === 'NumpadSubtract') {
        event.preventDefault();
        bump(-1);
      } else if (key === '0' || code === 'Numpad0') {
        event.preventDefault();
        saveZoom(DEFAULT_ZOOM, pageRef.current);
        applyZoom(DEFAULT_ZOOM);
        settle();
      }
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('keydown', onKey);
    };
  }, []);
}
